import React from "react";
import TitleSection from "../Components/TitleSection";
import Container from "../Components/Container";
import { LinkAndCancelButton, STEPS } from "../utils";
import { Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { addMinutes, format } from "date-fns";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { useRescheduling } from "../ReschedulingProvider";

export default function Recommendations() {
  const theme = useTheme();
  const {
    setStep,
    onBack,
    reschedulingHistory,
    intersectionAvailability,
    callInfo,
    setNewAppointmentTime,
    clientMakeupDays,
    clinicianAvailableAppointmentTimes,
  } = useRescheduling();
  const originalCallDuration = callInfo?.call_duration;
  const showGoHome = reschedulingHistory.length === 1;
  const hasMakeupDay = clientMakeupDays.length > 0;
  const maximumRecommendations = hasMakeupDay ? 2 : 3;
  const originalSessionDate = new Date(callInfo?.scheduled_date);

  const recommendations = React.useMemo(() => {
    const nonOverlappingSlots = [];
    if (intersectionAvailability.length > 0) {
      // Always include the first available slot
      nonOverlappingSlots.push({ date: intersectionAvailability[0].slot });

      // NOTE: not sorting the slots here, as they are already sorted by the API
      // For each subsequent slot, check if it overlaps with any already selected slot
      for (let i = 1; i < intersectionAvailability.length; i++) {
        const currentSlot = new Date(intersectionAvailability[i].slot);
        let overlaps = false;

        for (const selectedSlot of nonOverlappingSlots) {
          const selectedStart = new Date(selectedSlot.date);
          const selectedEnd = addMinutes(selectedStart, originalCallDuration);

          // Check if current slot starts before the end of any selected slot
          const currentEnd = addMinutes(currentSlot, originalCallDuration);
          if (
            (currentSlot >= selectedStart && currentSlot < selectedEnd) ||
            (selectedStart >= currentSlot && selectedStart < currentEnd)
          ) {
            overlaps = true;
            break;
          }
        }

        if (!overlaps) {
          nonOverlappingSlots.push({ date: intersectionAvailability[i].slot });
        }
      }
    }

    // Sort by proximity to original session date
    const clinicianAvailableAppointmentTimesSorted = [...clinicianAvailableAppointmentTimes].sort(
      (a, b) => {
        const dateA = new Date(a.startDateTime);
        const dateB = new Date(b.startDateTime);
        const diffA = Math.abs(dateA.getTime() - originalSessionDate.getTime());
        const diffB = Math.abs(dateB.getTime() - originalSessionDate.getTime());

        return diffA - diffB;
      }
    );

    // Inject missing slots from clinicianAvailableAppointmentTimes if there are less than the maximum recommendations
    if (nonOverlappingSlots.length < maximumRecommendations) {
      const missingSlots = maximumRecommendations - nonOverlappingSlots.length;
      let addedSlots = 0;
      let previouslyAddedSlot = null;
      for (let i = 0; i < clinicianAvailableAppointmentTimesSorted.length; i++) {
        if (addedSlots >= missingSlots) break;
        const previousEnd = previouslyAddedSlot
          ? addMinutes(previouslyAddedSlot, originalCallDuration)
          : null;
        const currentSlot = new Date(clinicianAvailableAppointmentTimesSorted[i].startDateTime);
        if ((i > 0 && previousEnd <= currentSlot) || !previouslyAddedSlot) {
          addedSlots++;
          previouslyAddedSlot = currentSlot;
          nonOverlappingSlots.push({
            date: clinicianAvailableAppointmentTimesSorted[i].startDateTime,
          });
        }
      }
    }

    return [
      ...nonOverlappingSlots.slice(0, maximumRecommendations),
      ...(hasMakeupDay ? [{ date: clientMakeupDays[0], isMakeup: true }] : []),
    ];
  }, [intersectionAvailability, originalCallDuration, clinicianAvailableAppointmentTimes]);

  return (
    <Container onBack={showGoHome ? undefined : onBack} showGoHome={showGoHome}>
      <TitleSection
        title={"Need to change your therapy session?"}
        subtitle={"Here are dates and times your BCBA is available to reschedule this appointment."}
      />
      <Stack gap={7}>
        <Stack gap={5}>
          {recommendations.map(({ date, isMakeup }, index) => {
            const formattedDate = format(new Date(date), "EEEE MMM do");
            const startTime = format(new Date(date), "h:mm a");
            const endTime = format(addMinutes(new Date(date), originalCallDuration), "h:mm a");
            return (
              <Stack
                key={index}
                p="8px 16px"
                gap={5}
                borderRadius="8px"
                border={`1px solid ${theme.palette.divider}`}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack py="4px">
                  {isMakeup && (
                    <Stack direction="row" gap={1} alignItems="center">
                      <ArrowsClockwise color={theme.palette.primary.main} />
                      <Typography variant="body2" color="primary.main">
                        Make-up Day
                      </Typography>
                    </Stack>
                  )}
                  <Typography variant="body1">{formattedDate}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {startTime} - {endTime}
                  </Typography>
                </Stack>
                <Button
                  color="secondary"
                  onClick={() => {
                    setNewAppointmentTime(date);
                    setStep(STEPS.review);
                  }}
                >
                  Update Now
                </Button>
              </Stack>
            );
          })}
        </Stack>
        <LinkAndCancelButton
          LinkLabel="Show more options"
          onClickLink={() => setStep(STEPS.customDate)}
          onCancel={() => setStep(STEPS.cancellationReview)}
        />
      </Stack>
    </Container>
  );
}
